<script setup lang="ts">
import LuckyRushContent from '@/components/LuckyRushContent.vue'
import { T } from '@tolgee/vue'
import TabButton from '@/components/TabButton.vue'
import { computed, onBeforeMount, ref, watch } from 'vue'
import { useHapticFeedback } from '@/composables/game/haptic-feedback'
import { LuckyRushTab } from '@/types/giveaway'
import { useGiveaway } from '@/composables/game/giveaway'
import LuckyRushBanner from '@/components/LuckyRushBanner.vue'

const { addHapticFeedback } = useHapticFeedback()
const {
  countdown,
  giveawayPreviousEndTimestamp,
  drawn,
  syncGiveaway,
} = useGiveaway()

const synced = ref(false)

const activeTab = ref<LuckyRushTab>(LuckyRushTab.LIVE)

const isLiveDrawFinished = computed(() => !countdown.value && drawn.value)

// Show tabs only when winners are drawn and there's previous data
const showTabs = computed(() => !countdown.value && drawn.value && !!giveawayPreviousEndTimestamp.value)

onBeforeMount(async () => {
  await syncGiveaway()
  synced.value = true
})

watch(synced, () => {
  if (isLiveDrawFinished.value && giveawayPreviousEndTimestamp.value) {
    activeTab.value = LuckyRushTab.PREVIOUS
  }
})

</script>
<template>
  <div class="pb-6 relative flex flex-col w-full gap-y-4 px-4 h-full pt-4">
    <div
      v-if="showTabs"
      class="flex w-full rounded-xl p-0.5 bg-dark-top-layer z-[3] relative"
    >
      <TabButton
        :is-active="activeTab === LuckyRushTab.LIVE"
        @click="addHapticFeedback(); activeTab = LuckyRushTab.LIVE"
      >
        <T
          key-name="lucky_rush.live"
          default-value="Live"
        />
      </TabButton>
      <TabButton
        :is-active="activeTab === LuckyRushTab.PREVIOUS"
        @click="addHapticFeedback(); activeTab = LuckyRushTab.PREVIOUS"
      >
        <div class="flex items-center gap-1.5 gold-text">
          <T
            key-name="lucky_rush.previous"
            default-value="Previous"
          />
        </div>
      </TabButton>
    </div>
    <div class="w-full h-auto flex items-center justify-center object-contain">
      <LuckyRushBanner
        is-single
        :has-tag="activeTab === LuckyRushTab.LIVE"
        :pulsate="!!countdown"
      />
    </div>
    <template v-if="synced">
      <div
        v-if="activeTab === LuckyRushTab.LIVE && isLiveDrawFinished"
        class="flex flex-col gap-y-2 items-center flex-1 h-full justify-center"
      >
        <h2 class="uppercase text-[32px] leading-8 font-semibold">
          <T
            key-name="lucky_rush.live.empty.title"
            default-value="NO ACTIVE GIVEAWAY"
          />
        </h2>
        <p class="whitespace-pre-line text-white/[70%] text-base font-semibold text-center">
          <T
            key-name="lucky_rush.live.empty.title"
            :default-value="`You just missed it!\nStay tuned for the next Lucky Rush.`"
          />
        </p>
      </div>
      <LuckyRushContent
        v-else
        :active-tab="activeTab"
      />
    </template>
  </div>
</template>